/**
 * Convert a `fetch` Response into a useful value for "file download" endpoints.
 *
 * On success (2xx), returns a **Blob** (e.g., an file).
 * On error (!2xx), tries to parse a **JSON** error body; if not JSON, falls back to text
 * wrapped as `{ detail: string }`. This keeps RTK Query error handling predictable.
 *
 * Params
 * - res (Response): The browser Fetch API Response object.
 *
 * Returns
 * - Promise<Blob | any>: Resolves to a Blob on success, or a JSON-like object on error.
 *
 * Behavior
 * - Success (res.ok === true): `await res.blob()` — suitable for `URL.createObjectURL` or file-saver flows.
 * - Error with JSON Content-Type: `await res.json()` — preserves structured error payloads (e.g., `{ code, detail }`).
 * - Error with non-JSON: reads text, attempts `JSON.parse(text)`, otherwise returns `{ detail: text }`.
 *
 * Notes
 * - Do **not** set `Content-Type` on GET requests; let the server decide.
 */
export async function fileResponseHandler(res) {
  if (res.ok) return await res.blob();

  const ct = (res.headers.get("content-type") || "").toLowerCase();

  // JSON errors (most APIs, including DRF, send these)
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      // Malformed JSON despite JSON content-type: fall through to text branch
    }
  }

  // Text or unknown content-type: try text -> JSON -> { detail }
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}
